import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TokenStorage } from '../lib/token-storage';
import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import { useUserStore } from '@/store/useUserStore';
import type { LLApiResponse, ShopperSessionData } from '../types/loyalty-lane';

const LL_SESSION_API = import.meta.env.VITE_LL_SESSION_API || '';
const LL_CLIENT_USERNAME = import.meta.env.VITE_LOYALTY_LANE_CLIENT_USERNAME || '';
const LL_CLIENT_PASSWORD = import.meta.env.VITE_LOYALTY_LANE_CLIENT_PASSWORD || '';
const LL_ADMIN_USERNAME = import.meta.env.VITE_LOYALTY_LANE_ADMIN_USERNAME || '';
const LL_ADMIN_PASSWORD = import.meta.env.VITE_LOYALTY_LANE_ADMIN_PASSWORD || '';

const BROKER_MAX_RETRIES = 2;
const BROKER_BASE_DELAY_MS = 1000;

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  streetaddress1: string;
  city: string;
  state: string;
  zipcode: string;
  homestore: string;
  birthdate?: string; // "MM/DD/YYYY", omitted when the shopper leaves it blank
}

export type AuthErrorKind = 'invalid_credentials' | 'network' | 'broker' | 'unknown';

export class AuthError extends Error {
  kind: AuthErrorKind;
  constructor(message: string, kind: AuthErrorKind) {
    super(message);
    this.name = 'AuthError';
    this.kind = kind;
  }
}

/** Extract human-readable message from JSON error bodies like {"error":"..."} */
function parseErrorMessage(body: string, fallback: string): string {
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed.error || parsed.message || parsed.detail || fallback;
    }
  } catch {
    // Not JSON — return body if it looks like a message, otherwise fallback
    if (body && body.length < 200 && !body.startsWith('{') && !body.startsWith('<')) {
      return body;
    }
  }
  return fallback;
}

interface RegistrationResponse {
  cardnumber: string;
  isNewAccount: boolean;
}

/**
 * Calls the registration Lambda to create a new LL account.
 * Returns { cardnumber } — does NOT return a session.
 * The client must sign in separately via POST /ShopperSession.
 */
async function fetchBrokerRegistration(
  url: string,
  profile: ProfileData,
): Promise<RegistrationResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= BROKER_MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, BROKER_BASE_DELAY_MS * attempt));
    }

    try {
      const payload = {
        profile,
        clientUsername: LL_CLIENT_USERNAME,
        clientPassword: LL_CLIENT_PASSWORD,
        adminUsername: LL_ADMIN_USERNAME,
        adminPassword: LL_ADMIN_PASSWORD,
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 409) {
        const body = await res.text().catch(() => '');
        throw new AuthError(
          parseErrorMessage(body, 'An account with this email already exists. Please sign in instead.'),
          'invalid_credentials',
        );
      }

      if (res.status >= 400 && res.status < 500) {
        const body = await res.text().catch(() => '');
        throw new AuthError(
          parseErrorMessage(body, `Registration failed (${res.status})`),
          'broker',
        );
      }

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        lastError = new AuthError(
          parseErrorMessage(body, `Registration failed (${res.status})`),
          'broker',
        );
        continue;
      }

      const data = await res.json() as RegistrationResponse;
      if (!data.cardnumber) {
        throw new AuthError('Registration succeeded but no card number was returned.', 'broker');
      }

      return data;
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.kind !== 'network') throw err;
        lastError = err;
      } else {
        lastError = new AuthError(
          'Unable to connect. Please check your connection and try again.',
          'network',
        );
      }
    }
  }

  throw lastError ?? new AuthError('Registration failed', 'broker');
}

export function useCouponAuth() {
  const queryClient = useQueryClient();

  /**
   * Sign in with email + password via LL's native POST /ShopperSession.
   * Returns the SST session data on success.
   */
  const signIn = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      let response;
      try {
        response = await loyaltyLaneClient.post<LLApiResponse<ShopperSessionData>>(
          'ShopperSession',
          { username: email, password },
          { skipAuth: false },
        );
      } catch (err) {
        // Map an auth failure from the client (thrown as a raw Error) to a friendly message.
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('401') || msg.includes('403')) {
          throw new AuthError('Invalid email or password.', 'invalid_credentials');
        }
        throw err;
      }

      const data = response.data;
      if (data.result !== 'OK' || !data.data?.token) {
        throw new AuthError(
          data.message || 'Invalid email or password.',
          'invalid_credentials',
        );
      }

      // Store SST and account info
      TokenStorage.setShopperSession(data.data);
      useUserStore.getState().setCouponProfile({
        shopperid: data.data.shopperid,
        accountid: data.data.accountid,
      });

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clipped-coupons'] });
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });

  /**
   * Create a new account via Lambda, then sign in with the new credentials.
   * Two-step: Lambda creates account → client authenticates via POST /ShopperSession.
   */
  const createAccount = useMutation({
    mutationFn: async ({ profile }: { profile: ProfileData }) => {
      const llSessionUrl = `${LL_SESSION_API.replace(/\/$/, '')}/ll-session`;
      const registration = await fetchBrokerRegistration(llSessionUrl, profile);

      // Now sign in with the email + password the user just set
      const response = await loyaltyLaneClient.post<LLApiResponse<ShopperSessionData>>(
        'ShopperSession',
        { username: profile.email, password: profile.password },
        { skipAuth: false },
      );

      const data = response.data;
      if (data.result !== 'OK' || !data.data?.token) {
        throw new AuthError(
          'Account created but sign-in failed. Please try signing in manually.',
          'broker',
        );
      }

      // Store SST and account info
      TokenStorage.setShopperSession(data.data);
      useUserStore.getState().setCouponProfile({
        cardNumber: registration.cardnumber,
        shopperid: data.data.shopperid,
        accountid: data.data.accountid,
      });

      return { ...data.data, cardnumber: registration.cardnumber };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clipped-coupons'] });
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });

  const logout = async () => {
    TokenStorage.clearShopperToken();
    useUserStore.getState().setCouponProfile(null);

    await Promise.allSettled([
      loyaltyLaneClient.delete('ShopperSession').catch(() => {}),
      queryClient.invalidateQueries({ queryKey: ['clipped-coupons'] }),
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] }),
    ]);
  };

  return {
    signIn,
    createAccount,
    logout,
    isAuthenticated: TokenStorage.isAuthenticated(),
  };
}
